import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../store/data';
import { useAudio } from '../hooks/useAudio';
import { useSay, useSpeakingKey } from '../hooks/useSay';
import { useHeaderSlot } from '../hooks/useHeaderSlot';
import { Icon, type IconName } from '../components/Icon';
import { CardCarousel } from '../components/CardCarousel';
import { SectionMascot } from '../components/Mascot';
import { CardShell } from '../components/CardShell';
import { SoundButton } from '../components/SoundButton';
import { FlagToggle } from '../components/FlagToggle';
import { ImageStack } from '../components/ImageStack';
import {
  firstImage,
  allImages,
  transcriptionSrc,
  transcriptionSrcEn,
  effectSounds,
  pickEffectSound
} from '../lib/image';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sfx';
import { preloadSounds } from '../lib/audio';
import { normalize } from '../lib/normalize';
import type { Word } from '../types';
import { syllabifySpanish } from '../lib/syllables';
import { syllableKey } from '../lib/phrases';
import './DetailView.css';
import { ICON_SIZE } from '../styles/tokens';
import { EmptyArt } from '../components/EmptyArt';

type Props = {
  category: string;
};

const NUM_OBJECT_SETS: { name: string; icon: IconName; color: string }[] = [
  { name: 'apple', icon: 'apple', color: 'var(--neo-red)' },
  { name: 'star', icon: 'star', color: 'var(--neo-yellow)' },
  { name: 'heart', icon: 'heart', color: 'var(--neo-pink)' },
  { name: 'sun', icon: 'sun', color: 'var(--neo-orange)' },
  { name: 'sparkles', icon: 'pop', color: 'var(--neo-cyan)' },
  { name: 'car', icon: 'car', color: 'var(--neo-blue)' },
  { name: 'flower', icon: 'flower', color: 'var(--neo-purple)' },
  { name: 'cat', icon: 'cat', color: 'var(--neo-lime)' },
  { name: 'dog', icon: 'dog', color: 'var(--neo-yellow-dark)' },
  { name: 'music', icon: 'music', color: 'var(--neo-pink-dark)' },
  { name: 'plane', icon: 'plane', color: 'var(--neo-cyan-dark)' },
  { name: 'pizza', icon: 'pizza', color: 'var(--neo-orange-dark)' },
  { name: 'cake', icon: 'cake', color: 'var(--neo-pink)' },
];

/* bg1/bg2: wash muy claro del tono `light` (~20%/8% sobre blanco), precalculado
   a mano en vez de color-mix() — Safari 16.0/16.1 (piso de compat del proyecto)
   no lo soporta. Antes `light` se calculaba y nunca se usaba: la tarjeta de
   NÚMERO quedaba gris/blanca en vez de mostrar el color del esquema. */
const NUM_COLOR_SCHEMES = [
  { dark: 'var(--c-blue)',     light: 'var(--neo-blue)',   shadow: 'var(--neo-shadow)', bg1: 'var(--c-blue-light)',   bg2: 'var(--neo-white)' }, // blue
  { dark: 'var(--neo-pink)',   light: 'var(--c-rose)',     shadow: 'var(--neo-shadow)', bg1: 'var(--c-pink-light)',   bg2: 'var(--neo-white)' }, // pink
  { dark: 'var(--neo-lime)',   light: 'var(--c-green)',    shadow: 'var(--neo-shadow)', bg1: 'var(--c-lime-light)',   bg2: 'var(--neo-white)' }, // lime
  { dark: 'var(--neo-orange)', light: 'var(--c-coral)',    shadow: 'var(--neo-shadow)', bg1: 'var(--c-orange-light)', bg2: 'var(--neo-white)' }, // orange
  { dark: 'var(--neo-purple)', light: 'var(--c-violet)',   shadow: 'var(--neo-shadow)', bg1: 'var(--c-purple-light)', bg2: 'var(--neo-white)' }, // purple
  { dark: 'var(--neo-red)',    light: 'var(--c-coral)',    shadow: 'var(--neo-shadow)', bg1: 'var(--c-coral-light)',  bg2: 'var(--neo-white)' }, // coral
  { dark: 'var(--c-indigo)',   light: 'var(--c-indigo)',   shadow: 'var(--neo-shadow)', bg1: 'var(--c-indigo-light)', bg2: 'var(--neo-white)' }, // indigo
  { dark: 'var(--neo-cyan)',   light: 'var(--neo-cyan)',   shadow: 'var(--neo-shadow)', bg1: 'var(--c-aqua-light)',   bg2: 'var(--neo-white)' }, // cyan
  { dark: 'var(--c-violet)',   light: 'var(--c-violet)',   shadow: 'var(--neo-shadow)', bg1: 'var(--c-violet-light)', bg2: 'var(--neo-white)' }, // violet
] as const;

const WORD_TO_DIGIT: Record<string, number> = {
  CERO: 0, UNO: 1, DOS: 2, TRES: 3, CUATRO: 4,
  CINCO: 5, SEIS: 6, SIETE: 7, OCHO: 8, NUEVE: 9,
  DIEZ: 10, ONCE: 11, DOCE: 12,
};

function digitOf(item: Word): number {
  if (item.numeral != null && String(item.numeral).trim() !== '') {
    const n = Number(item.numeral);
    if (Number.isFinite(n)) return Math.max(0, Math.min(12, Math.floor(n)));
  }
  return WORD_TO_DIGIT[item.word?.toUpperCase()] ?? 0;
}


function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function formatCatLabel(tag: string): string {
  if (tag === 'NUMERO') return 'Números';
  if (tag === 'TODAS') return 'Todas';
  return tag.charAt(0) + tag.slice(1).toLowerCase();
}

const wordKey = (item: Word) => `palabra:${item.id ?? item.word}`;
const sylKey = (item: Word, idx: number) => `silaba-flash:${item.id ?? item.word}:${idx}`;

function getAdultPromptForWord(item: Word): string | null {
  const isEmocion = item.tags?.includes('EMOCIONES');
  const isMente = item.tags?.includes('PALABRAS DE LA MENTE');
  if (!isEmocion && !isMente) return null;

  const w = (item.word || '').toUpperCase().trim();
  if (isEmocion) {
    if (w === 'CONTENTO' || w === 'ALEGRE') return '¿Qué cosas te ponen muy contenta a vos?';
    if (w === 'TRISTE') return '¿Qué te ayuda cuando estás triste? ¿A quién le pedís un abrazo?';
    if (w === 'ENOJADO' || w === 'RABIA') return '¿Qué hacés cuando te da rabia? ¿Respirar hondo ayuda?';
    if (w === 'ASUSTADO' || w === 'MIEDO') return '¿A qué le tenías miedo de chiquita? ¿Y ahora qué hacés?';
    if (w === 'SORPRENDIDO') return '¿Qué sorpresa linda te gustaría tener hoy?';
    if (w === 'VERGÜENZA' || w === 'CON VERGÜENZA') return '¿Alguna vez te dio vergüenza hablar? ¡A todos nos pasa!';
    return '¿Cuándo te sentiste así? ¿Cómo se nota en tu cara?';
  }

  if (isMente) {
    if (w === 'PENSAR') return '¿En qué estás pensando ahora mismo?';
    if (w === 'SABER') return '¿Quién sabe dónde están tus juguetes favoritos?';
    if (w === 'CREER') return '¿Alguna vez creíste que había algo y era otra cosa?';
    if (w === 'OLVIDAR' || w === 'OLVIDARSE') return '¿Qué es lo que a veces se le olvida a papá o a mamá?';
    if (w === 'QUERER') return '¿Qué es lo que más querés hacer hoy?';
    if (w === 'SORPRENDERSE') return '¿Qué fue lo último que te sorprendió mucho?';
    return '¿Qué pensás sobre esto? ¿Quién más lo sabe?';
  }

  return null;
}

export function DetailView({ category }: Props) {
  const { byTag, allWords } = useData();
  const { stop } = useSay();
  const headerSlot = useHeaderSlot();
  const isAll = category === 'TODAS';

  const [filterCat, setFilterCat] = useState<string | null>(null);
  const [englishMap, setEnglishMap] = useState<Record<string, boolean>>({});

  // Ordenamiento aleatorio por defecto cada vez que se entra + opción A-Z
  const [sortMode, setSortMode] = useState<'random' | 'alpha'>('random');
  const [shuffleSeed, setShuffleSeed] = useState(0);

  // Búsqueda en tiempo real
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filterTags = useMemo(() => {
    if (!isAll) return [];
    return Object.keys(byTag)
      .filter((k) => k !== 'TODAS' && (byTag[k]?.length ?? 0) > 0)
      .sort();
  }, [isAll, byTag]);

  // Lista aleatorizada base generada en cada sesión/montaje
  const shuffledAllWords = useMemo(() => {
    return shuffle(allWords);
  }, [allWords, shuffleSeed]);

  const baseItems = useMemo(() => {
    let list: Word[];
    if (category === 'TODAS') {
      list = sortMode === 'alpha'
        ? [...allWords].sort((a, b) => (a.word || '').localeCompare(b.word || '', 'es'))
        : shuffledAllWords;
    } else if (category === 'NUMERO') {
      const nums = byTag['NUMERO'] ?? [];
      list = [...nums].sort((a, b) => digitOf(a) - digitOf(b));
    } else {
      const catList = byTag[category] ?? [];
      list = sortMode === 'alpha'
        ? [...catList].sort((a, b) => (a.word || '').localeCompare(b.word || '', 'es'))
        : shuffle(catList);
    }
    return list;
  }, [category, byTag, allWords, shuffledAllWords, sortMode]);

  const items = useMemo(() => {
    let res = baseItems;
    if (isAll && filterCat) {
      res = res.filter((w) => w.tags?.includes(filterCat));
    }
    const q = normalize(searchQuery.trim());
    if (q) {
      res = res.filter((w) =>
        normalize(w.word || '').includes(q) ||
        normalize(w.word_en || '').includes(q)
      );
    }
    return res;
  }, [baseItems, isAll, filterCat, searchQuery]);

  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    setCurrentIdx(0);
    setFilterCat(null);
    setEnglishMap({});
  }, [category]);

  const toggleSort = () => {
    haptic('light');
    playSfx('tap');
    if (sortMode === 'random') {
      setSortMode('alpha');
    } else {
      setSortMode('random');
      setShuffleSeed((s) => s + 1);
    }
    setCurrentIdx(0);
  };

  const isNumero = category === 'NUMERO';
  const isColor = category === 'COLOR';

  const variantMap = useRef<Record<number, number>>({});

  useEffect(() => {
    // Always generate variants — needed for NUMERO cards in any view (TODAS included)
    const map: Record<number, number> = {};
    for (let i = 0; i < items.length; i++) {
      map[i] = Math.floor(Math.random() * 100000);
    }
    variantMap.current = map;
  }, [category, items.length]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    stop();
  }, [currentIdx, stop]);

  // La palabra de la tarjeta actual y de las vecinas, cargadas antes del toque.
  useEffect(() => {
    const near = items.slice(Math.max(0, currentIdx - 1), currentIdx + 2);
    preloadSounds(near.flatMap((w) => [transcriptionSrc(w), transcriptionSrcEn(w)]));
  }, [items, currentIdx]);

  const goPrev = useCallback(() => {
    haptic('light');
    playSfx('page');
    setCurrentIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const goNext = useCallback(() => {
    haptic('light');
    playSfx('page');
    setCurrentIdx((prev) => Math.min(items.length - 1, prev + 1));
  }, [items.length]);

  const getCardKey = useCallback((item: Word, i: number) => {
    return item.id != null ? String(item.id) : `${item.word}-${i}`;
  }, []);

  const toggleEnglish = useCallback((item: Word, i: number) => {
    haptic('light');
    const key = item.id != null ? String(item.id) : `${item.word}-${i}`;
    setEnglishMap((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const headerActions = (
    <>
      <button
        type="button"
        className={`chip${sortMode === 'alpha' ? ' is-active' : ''}`}
        onClick={toggleSort}
        title={sortMode === 'alpha' ? 'Orden alfabético A-Z (toca para mezclar al azar)' : 'Orden aleatorio (toca para ordenar A-Z)'}
        aria-label={sortMode === 'alpha' ? 'Ordenar aleatoriamente' : 'Ordenar alfabéticamente'}
      >
        <Icon name={sortMode === 'alpha' ? 'letter' : 'refresh'} size={ICON_SIZE.sm} />
        <span className="chip-label">{sortMode === 'alpha' ? 'A-Z' : 'Azar'}</span>
      </button>

      <button
        type="button"
        className={`icon-btn${searchOpen || searchQuery ? ' is-active' : ''}`}
        onClick={() => {
          haptic('light');
          playSfx('tap');
          setSearchOpen((v) => !v);
          if (searchOpen && searchQuery) {
            setSearchQuery('');
            setCurrentIdx(0);
          }
        }}
        title="Buscar tarjetas"
        aria-label="Buscar"
      >
        <Icon name="search" size={ICON_SIZE.sm} />
      </button>
    </>
  );

  return (
    <div className="detail">
      {headerSlot && createPortal(headerActions, headerSlot)}

      <div className="detail-toolbar">
        {isAll && filterTags.length > 0 && (
          <div className="detail-filter-bar">
            <button
              type="button"
              className={`chip${filterCat === null ? ' is-active' : ''}`}
              onClick={() => { haptic('light'); playSfx('tap'); setFilterCat(null); setCurrentIdx(0); }}
              aria-pressed={filterCat === null}
            >
              Todas
            </button>
            {filterTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`chip${filterCat === tag ? ' is-active' : ''}`}
                onClick={() => { haptic('light'); playSfx('tap'); setFilterCat(tag); setCurrentIdx(0); }}
                aria-pressed={filterCat === tag}
              >
                {formatCatLabel(tag)}
              </button>
            ))}
          </div>
        )}
      </div>

      {searchOpen && (
        <div className="detail-search-box">
          <div className="detail-search-input-wrap">
            <Icon name="search" size={ICON_SIZE.md} className="detail-search-icon" />
            <input
              type="text"
              className="detail-search-input"
              placeholder="Buscar palabra..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentIdx(0);
              }}
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                className="icon-btn detail-search-clear"
                onClick={() => {
                  setSearchQuery('');
                  setCurrentIdx(0);
                }}
                aria-label="Limpiar búsqueda"
              >
                <Icon name="close" size={ICON_SIZE.sm} />
              </button>
            )}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <EmptyArt icon="book" />
          <h2 className="empty-state-title">
            {searchQuery ? `No encontramos "${searchQuery}"` : 'Todavía no hay tarjetas acá'}
          </h2>
          <div className="empty-state-sub">
            {searchQuery ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { setSearchQuery(''); setCurrentIdx(0); }}
              >
                Limpiar búsqueda
              </button>
            ) : (
              'Pedile a un grande que cargue algunas en el panel de admin.'
            )}
          </div>
        </div>
      ) : (
        <CardCarousel
          items={items}
          getKey={getCardKey}
          renderCard={(item, isActive, i) => {
            const key = getCardKey(item, i);
            const isEnglish = !!englishMap[key];
            return (
              <DetailCard
                item={item}
                isActive={isActive}
                isNumero={isNumero}
                isColor={isColor}
                variant={variantMap.current[i] ?? 0}
                isEnglish={isEnglish}
                onToggleEnglish={() => toggleEnglish(item, i)}
              />
            );
          }}
          currentIdx={currentIdx}
          onIndexChange={setCurrentIdx}
          onPrev={goPrev}
          onNext={goNext}
          windowSize={2}
          slotWidthPercent={80}
          aside={<SectionMascot section="tarjetas" />}
        />
      )}
    </div>
  );
}

type DetailCardProps = {
  item: Word;
  isActive: boolean;
  isNumero?: boolean;
  isColor?: boolean;
  variant?: number;
  isEnglish: boolean;
  onToggleEnglish: () => void;
};

/** Fila fija de acciones bajo el contenido de la tarjeta (mismo lugar que
 *  `.letter-actions` en Letras y el pill de Sílabas). El switch de inglés
 *  no vive acá: es un estado de la tarjeta, no una lectura, y se queda en
 *  su badge de esquina (`card-flag-toggle`). */
function CardActions({
  item,
  onPlayWord,
  isWordSpeaking,
  isEnglish,
}: {
  item: Word;
  onPlayWord: () => void;
  isWordSpeaking?: boolean;
  isEnglish?: boolean;
}) {
  const audio = useAudio();
  const effects = effectSounds(item);
  const hasEffects = effects.length > 0;
  const hasMultiple = effects.length > 1;

  const onPlayEffect = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic('light');
    const src = pickEffectSound(item);
    if (src) audio.play(src);
  };

  const handlePlayWord = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPlayWord();
  };

  const wordName = isEnglish && item.word_en ? item.word_en : item.word;

  return (
    <div className="card-actions">
      {/* Botón 1: Transcripción / lectura de la palabra (voz grabada o TTS) */}
      <SoundButton
        icon="volume"
        label="Palabra"
        color="brand"
        onClick={handlePlayWord}
        className={isWordSpeaking ? 'is-speaking' : undefined}
        ariaLabel={`Escuchar transcripción de ${wordName}`}
        title={`Escuchar transcripción de ${wordName}`}
      />

      {/* Botón 2: El sonido / efecto sonoro que hace la tarjeta (ladrido, clip de música, etc.) */}
      {hasEffects && (
        <SoundButton
          icon="music"
          label="Sonido"
          color="accent"
          onClick={onPlayEffect}
          ariaLabel={
            hasMultiple
              ? `Sonido que hace la tarjeta (${effects.length} variantes)`
              : 'Sonido que hace la tarjeta'
          }
          title={
            hasMultiple
              ? `Sonido que hace la tarjeta (${effects.length} variantes)`
              : 'Sonido que hace la tarjeta'
          }
          badgeCount={effects.length}
        />
      )}
    </div>
  );
}

function DetailCard({
  item,
  isActive,
  isNumero,
  isColor,
  variant = 0,
  isEnglish,
  onToggleEnglish
}: DetailCardProps) {
  const images = useMemo(() => allImages(item), [item]);
  const { letterSounds } = useData();
  const { say } = useSay();
  const speaking = useSpeakingKey();
  const isWordSpeaking = speaking === wordKey(item);

  const hasEnglish = !!item.word_en;
  const rawWord = isEnglish && item.word_en ? item.word_en : item.word;
  const displayWord = rawWord || '';
  const displayWordUpper = displayWord.toUpperCase();
  const displayWordLower = displayWord.toLowerCase();

  const playWord = useCallback(() => {
    if (!isActive) return;
    haptic('light');
    say(isEnglish && item.word_en
      ? { key: wordKey(item), text: item.word_en, src: transcriptionSrcEn(item), lang: 'en-US' }
      : { key: wordKey(item), text: item.word, src: transcriptionSrc(item) });
  }, [isActive, item, say, isEnglish]);

  const adultPrompt = useMemo(() => getAdultPromptForWord(item), [item]);

  const syllables = useMemo(() => {
    if (isEnglish || !displayWord) return [];
    const syls = syllabifySpanish(displayWord);
    return syls.length > 1 ? syls : [];
  }, [isEnglish, displayWord]);

  const playSyllable = useCallback((syl: string, idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    haptic('light');
    say({
      key: sylKey(item, idx),
      text: syl.toLowerCase(),
      // Las sílabas directas (BA, ÑO…) ya están grabadas en Letras; las
      // trabadas o con coda (TREN, PAN) caen a TTS.
      src: letterSounds[syllableKey(syl)],
    });
  }, [item, say, letterSounds]);

  const imgSrc = images[0] || firstImage(item);

  const colorStyle: React.CSSProperties = (isColor && item.color_hex)
    ? {
        background: `linear-gradient(135deg, ${item.color_hex} 0%, ${item.color_hex}CC 100%)`,
        color: 'var(--neo-white)',
      }
    : {};

  // ── NUMERO ───────────────────────────────────────────────────────────────
  if (isNumero) {
    const safeN = digitOf(item);
    const numeralStr = String(safeN);
    const activeIconObj = NUM_OBJECT_SETS[variant % NUM_OBJECT_SETS.length];
    const scheme = NUM_COLOR_SCHEMES[variant % NUM_COLOR_SCHEMES.length];
    const numStyle = {
      '--num-color-dark':   scheme.dark,
      '--num-color-shadow': scheme.shadow,
      '--num-color-bg1':    scheme.bg1,
      '--num-color-bg2':    scheme.bg2,
    } as React.CSSProperties;

    return (
      <CardShell isActive={isActive} className={isEnglish ? 'card-english' : undefined} style={numStyle}>
        {hasEnglish && (
          <FlagToggle active={isEnglish} onToggle={onToggleEnglish} className="card-flag-toggle" />
        )}

        {/* Visual area */}
        <button
          type="button"
          className="btn card-image-btn num-card-body"
          onClick={playWord}
          aria-label={displayWord}
        >
          <div className="num-digit">{numeralStr}</div>
          {safeN > 0 && (
            <div className="num-emoji-grid">
              {Array.from({ length: safeN }, (_, i) => (
                <span
                  key={`${variant}-${i}`}
                  className="num-emoji"
                  style={{ '--i': i } as React.CSSProperties}
                >
                  <Icon
                    name={activeIconObj.icon}
                    size={ICON_SIZE.xl}
                    fill={activeIconObj.color}
                    color="var(--neo-black)"
                  />
                </span>
              ))}
            </div>
          )}
        </button>

        <div
          className={`card-word${isWordSpeaking ? ' is-speaking' : ''}`}
          onClick={playWord}
          role="button"
          tabIndex={0}
        >
          <span className="card-word-upper">{displayWordUpper}</span>
          <span className="card-word-lower">{displayWordLower}</span>
          {syllables.length > 1 && (
            <div className="card-syllables-row" aria-label={`Sílabas de ${displayWord}`}>
              {syllables.map((syl, idx) => {
                const isSpeakingSyl = speaking === sylKey(item, idx);
                return (
                  <button
                    key={`${syl}-${idx}`}
                    type="button"
                    className={`btn btn--key card-syllable-chip${isSpeakingSyl ? ' is-speaking' : ''}`}
                    onClick={(e) => playSyllable(syl, idx, e)}
                    aria-label={`Sílaba ${syl}`}
                  >
                    {syl.toUpperCase()}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <CardActions
          item={item}
          onPlayWord={playWord}
          isWordSpeaking={isWordSpeaking}
          isEnglish={isEnglish}
        />
      </CardShell>
    );
  }

  // ── Default ──────────────────────────────────────────────────────────────
  return (
    <CardShell
      isActive={isActive}
      className={isEnglish ? 'card-english' : undefined}
      style={colorStyle}
    >
      {hasEnglish && (
        <FlagToggle active={isEnglish} onToggle={onToggleEnglish} className="card-flag-toggle" />
      )}

      {images.length > 1 ? (
        <ImageStack
          images={images}
          alt={displayWord}
          onPlay={playWord}
          className="card-image-stack"
        />
      ) : (
        <button
          type="button"
          className="btn card-image-btn"
          onClick={playWord}
          aria-label={displayWord}
        >
          {imgSrc ? (
            <img src={imgSrc} alt={displayWord} className="card-image" loading="lazy" decoding="async" />
          ) : (
            <div className="card-image-placeholder">
              <Icon name="star" size={ICON_SIZE.hero} />
            </div>
          )}
        </button>
      )}

      <div
        className={`card-word${isColor && item.color_hex ? ' card-word-on-color' : ''}${isWordSpeaking ? ' is-speaking' : ''}`}
        onClick={playWord}
        role="button"
        tabIndex={0}
      >
        <span className="card-word-upper">{displayWordUpper}</span>
        <span className="card-word-lower">{displayWordLower}</span>
        {syllables.length > 1 && (
          <div className="card-syllables-row" aria-label={`Sílabas de ${displayWord}`}>
            {syllables.map((syl, idx) => {
              const isSpeakingSyl = speaking === sylKey(item, idx);
              return (
                <button
                  key={`${syl}-${idx}`}
                  type="button"
                  className={`btn btn--key card-syllable-chip${isSpeakingSyl ? ' is-speaking' : ''}`}
                  onClick={(e) => playSyllable(syl, idx, e)}
                  aria-label={`Sílaba ${syl}`}
                >
                  {syl.toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {adultPrompt && (
        <div className="card-adult-prompt" role="note" aria-label="Guía para charlar en familia">
          <span className="card-adult-prompt-badge">Para charlar</span>
          <span className="card-adult-prompt-text">{adultPrompt}</span>
        </div>
      )}

      <CardActions
        item={item}
        onPlayWord={playWord}
        isWordSpeaking={isWordSpeaking}
        isEnglish={isEnglish}
      />
    </CardShell>
  );
}
